import { useState, useRef, useEffect } from "react";

// ─── CONFIG ───
// No API keys in frontend. All calls go through /api serverless functions.

// ─── SYSTEM PROMPTS ───

const DESIGNER_PROMPT = `You are a designer at Yours Truly. Someone wants to create a personalized design for someone they love. You're gathering the creative brief — the elements, references, and direction you need to make something amazing.

You are NOT a therapist. You are NOT interviewing them about feelings. You are an artist figuring out what goes INTO the design.

Flow:
1. First, ask who this is for — get the name.
2. Then ask what elements they want included — things this person loves, symbols that represent them, animals, hobbies, objects, anything visual.
3. Ask if there are any specific quotes, words, or phrases they want woven into the design.
4. Ask about the overall vibe or mood — dark and dramatic, warm and soft, bold and colorful, magical, elegant, etc.
5. Ask what should be the first thing someone notices, and what's the hidden detail they'd discover on a closer look.
6. Ask if there are any easter eggs or inside jokes to include — things only the recipient would catch.

Rules:
- Ask ONE question at a time. Never stack multiple questions.
- Keep responses to 1-2 sentences. Quick creative conversation, not paragraphs.
- Be casual and creative. Talk like a designer excited about a project.
- Don't assume gender until they tell you.
- Don't ask about feelings or emotions. Ask about concrete things you can PUT in the design.
- If they give short answers, that's fine. Work with what they give you and move to the next question.
- Don't use emojis.
- After you have enough to work with (elements, mood, hierarchy, and optionally quotes/easter eggs — usually after 5-7 exchanges), end your message with [READY] on its own line. Before the tag, say something brief like "Got it. I have everything I need — let me create something."
- Do NOT say [READY] too early. You need at minimum: the person's name, 3-4 concrete elements, and the overall mood.
- Your very first message should ask for the person's name if they haven't given it, or acknowledge the name and immediately ask what elements they want included.`;

const IMAGE_PROMPT_GENERATOR = `You are the Sculptor Brain at Yours Truly. Based on the conversation below, generate TWO hyper-detailed image prompts — one for a 3D sculpture and one for a flat acrylic art piece.

═══ YOUR DESIGN METHOD ═══

Step 1 — DOMINANT FORM:
Pick ONE recognizable form (animal, creature, natural structure, object) that best represents the overall essence of the elements. NOT abstract. NOT a human. The form should be something a person sees and immediately recognizes.

Step 2 — ELEMENT MAPPING via TRIPLE-MATCH:
Map each element from the conversation to a SPECIFIC body part or area of the dominant form. Every mapping MUST pass all three:
- GEOMETRIC match: the shapes are physically similar
- FUNCTIONAL match: they serve similar purposes
- EMOTIONAL match: they evoke similar feelings
If all three don't match, find a different mapping. Elements must be MORPHED INTO the form — they ARE part of the body, not decorations sitting beside it.

Step 3 — HIERARCHY:
Primary elements = large, immediately visible, prominent placement (chest, head, mane, wings).
Hidden elements = subtle, discoverable on close inspection (inside a curl, woven into texture, reflected in an eye).

═══ VISUAL LANGUAGE (this is what makes our designs unique) ═══

MATERIALS & SURFACES:
- The form is made of hyper-detailed CRYSTALLINE surfaces — faceted like cut precious gemstones (sapphire, amethyst, emerald, diamond)
- Surfaces REFRACT light — you should see light bending through translucent crystal, creating prismatic color shifts
- Mix of transparent and opaque crystalline sections creating depth
- Gold, rose gold, or bronze metallic accents for molecular details, beads, connection points
- Internal glow — light emanates from WITHIN the form, not just hitting the surface

COLOR PALETTES (choose based on mood):
- Magical/Elegant: deep amethyst purple + warm gold + iridescent blue
- Bold/Powerful: sapphire blue + emerald green + gold
- Warm/Loving: rose gold + amber + soft coral + warm white
- Dark/Dramatic: obsidian black + deep purple + gold lightning accents
- Ethereal/Dreamy: opalescent white + soft lavender + silver + touches of gold

STRUCTURAL DETAILS:
- Elements transition SEAMLESSLY — a mane becomes music notes, fur becomes flowing water, wings become book pages. The transition is organic, not a hard cut.
- Scattered particles float around the form — crystalline dust, molecular beads, light fragments breaking free
- Fine flowing energy trails or light lines connect related elements
- Molecular bead chains (small metallic spheres connected by thin rods) can frame the base or orbit the form

═══ GOLD-STANDARD EXAMPLES (study these — this is the quality bar) ═══

EXAMPLE 1 — "Sapphire Elephant" (for Shashikala):
Dominant form: elephant. Her love of wisdom mapped to the large forehead (geometric: both are broad surfaces; functional: both hold knowledge; emotional: both represent depth). Her garden mapped to the ears (geometric: both fan outward; functional: both receive; emotional: both are nurturing). Rendered in deep sapphire blue crystal with gold tusks and molecular bead chain around the base.

EXAMPLE 2 — "Bond Helix" (for Deepali):
Dominant form: double helix DNA structure. Her two cats (Kova & Kiara, named after Lion King characters) are curled together INSIDE the helix at the center — one dark, one golden, sleeping with tails intertwined. The helix strands are amethyst purple and iridescent blue crystalline, with gold molecular beads as rungs. Musical notes from Sleeping at Last grow organically from the strands like crystalline branches. The base flares into Kathak-inspired crystalline pleated petals. The top of the helix opens and dissolves into particles — a crown still forming, still manifesting. Elements: cats INSIDE the helix (not beside it), music GROWING FROM the strands (not floating nearby), manifestation shown through the incomplete crown.

EXAMPLE 3 — "Emerald Peacock" (for Priya):
Dominant form: peacock. Each tail feather eye contains a different element from her life — one has a tiny book, one has a musical note, one has a star. Rendered in emerald green crystal with gold filigree detail. The tail fans outward in faceted crystalline feathers that catch light differently at every angle.

═══ ANTI-PATTERNS (never do these) ═══
- NO text, words, or letters anywhere in the image
- NO food elements
- NO abstract dominant forms (no "swirl of energy" or "flowing ribbons")
- NO elements floating beside the form — everything must be PART of the form
- NO generic animal poses — the form should have character and intention
- NO flat or matte surfaces — everything is crystalline, faceted, light-catching
- NO pedestals that are plain circles — the base should be a round black marble pedestal with subtle detail (molecular beads, energy rings, or light reflection)
- NO cluttered compositions — one clear dominant form, not a collage

═══ PROMPT QUALITY BAR ═══
Each prompt MUST be 150-250 words of hyper-specific detail. Describe:
- The exact dominant form and its pose/orientation
- Where EACH element appears on the form and how it transitions
- The specific crystal/gemstone types and how they refract light
- The color palette with specific tones
- What particles/details float around the form
- The base/pedestal description
- The lighting setup

═══ OUTPUT FORMAT ═══
Respond ONLY with valid JSON (no markdown, no backticks):

{
  "name": "the person's name",
  "dominantForm": "what you chose and one sentence explaining why",
  "elementMappings": [
    {
      "element": "element name",
      "mappedTo": "specific body part or area",
      "tripleMatch": "geometric + functional + emotional explanation in one sentence",
      "prominence": "primary or hidden"
    }
  ],
  "colorPalette": "the specific palette chosen and why",
  "mood": "the mood/vibe from the conversation",
  "sculpturePrompt": "FULL 150-250 word hyper-detailed prompt following all rules above. End with: Rendered as a luxurious hyper-detailed 3D crystal sculpture on a round black marble pedestal with subtle golden molecular bead detail. Museum photography with dramatic side lighting creating prismatic refractions through the crystalline surfaces. Pure black background. Every surface is faceted like cut precious gemstones. Aspect ratio 3:4. High resolution. No text anywhere.",
  "plaquePrompt": "FULL 150-250 word hyper-detailed prompt for the SAME design as flat art. Use the SAME elements, same mappings, same composition — but rendered differently. End with: Rendered as luminous 2D crystalline particle art on pure black background for printing on acrylic. The form is made of thousands of flowing light trails, scattered luminous particles, and fine crystalline energy lines. Colors are deep and saturated gemstone tones. Particles scatter outward from the form and dissolve into the black. NOT a 3D render. No pedestal. No base. Bottom 20% pure black empty space for text overlay. Aspect ratio 3:4. High resolution. No text anywhere.",
  "poeticNote": "2-3 short sentences telling the recipient what to look for. Like: Look closer. Kova and Kiara are sleeping safe inside the helix. The music flows through the strands if you follow them upward."
}`;

// ─── COMPONENT ───

function Studio() {
  const [stage, setStage] = useState("opening");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [designData, setDesignData] = useState(null);
  const [sculptureImg, setSculptureImg] = useState(null);
  const [plaqueImg, setPlaqueImg] = useState(null);
  const [creationPhase, setCreationPhase] = useState(0);
  const [genError, setGenError] = useState(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (stage === "chat" && inputRef.current) inputRef.current.focus();
  }, [stage, loading]);

  // ─── API CALLS (through /api serverless functions — no keys exposed) ───

  async function chatGPT(msgs, system) {
    const res = await fetch("/api/studio-chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages: msgs, system }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data.content || "";
  }

  async function generateImage(prompt) {
    const res = await fetch("/api/studio-image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data.image;
  }

  // ─── CONVERSATION HANDLER ───

  async function handleSend() {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");

    if (stage === "opening") {
      setStage("chat");
      const newMsgs = [{ role: "user", content: userMsg }];
      setMessages(newMsgs);
      setLoading(true);
      try {
        const reply = await chatGPT(newMsgs, DESIGNER_PROMPT);
        const isReady = reply.includes("[READY]");
        const clean = reply.replace("[READY]", "").trim();
        setMessages((prev) => [...prev, { role: "assistant", content: clean }]);
        if (isReady) setTimeout(() => handleDesignPhase([...newMsgs, { role: "assistant", content: clean }]), 2000);
      } catch (e) {
        console.error(e);
        setMessages((prev) => [...prev, { role: "assistant", content: "Nice — what's their name?" }]);
      }
      setLoading(false);
      return;
    }

    const newMsgs = [...messages, { role: "user", content: userMsg }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      const reply = await chatGPT(newMsgs, DESIGNER_PROMPT);
      const isReady = reply.includes("[READY]");
      const clean = reply.replace("[READY]", "").trim();
      const updated = [...newMsgs, { role: "assistant", content: clean }];
      setMessages(updated);
      if (isReady) setTimeout(() => handleDesignPhase(updated), 2500);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  // ─── DESIGN GENERATION PHASE ───

  async function handleDesignPhase(conversationMsgs) {
    setStage("creating");
    setCreationPhase(0);
    setGenError(null);

    try {
      // Phase 1: Extract elements and generate prompts
      setCreationPhase(1);
      const convoText = conversationMsgs.map((m) => `${m.role}: ${m.content}`).join("\n");
      const designReply = await chatGPT(
        [{ role: "user", content: convoText }],
        IMAGE_PROMPT_GENERATOR
      );
      const parsed = JSON.parse(designReply.replace(/```json|```/g, "").trim());
      setDesignData(parsed);

      // Phase 2: Generate sculpture image
      setCreationPhase(2);
      const sculptureUrl = await generateImage(parsed.sculpturePrompt);
      setSculptureImg(sculptureUrl);

      // Phase 3: Generate plaque/acrylic image
      setCreationPhase(3);
      const plaqueUrl = await generateImage(parsed.plaquePrompt);
      setPlaqueImg(plaqueUrl);

      // Phase 4: Done
      setCreationPhase(4);
      setTimeout(() => setStage("results"), 800);
    } catch (e) {
      console.error(e);
      setGenError(e.message);
      setStage("results");
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // ─── OPENING ───
  if (stage === "opening") {
    return (
      <div style={S.screen}>
        <div style={S.center}>
          <div style={S.brand}>YOURS TRULY</div>
          <h1 style={S.heroText}>Tell me about someone you love.</h1>
          <div style={S.inputRow}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="My best friend, my mom, my partner..."
              style={S.textInput}
              autoFocus
            />
            <button onClick={handleSend} style={{ ...S.sendBtn, opacity: input.trim() ? 1 : 0.3 }}>→</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── CREATING ───
  if (stage === "creating") {
    const phases = [
      "Listening to the inspirations...",
      "Building the design blueprint...",
      "Creating the sculpture...",
      "Creating the acrylic art...",
      "Finishing touches...",
    ];
    return (
      <div style={S.screen}>
        <div style={S.center}>
          <div style={S.brand}>YOURS TRULY</div>
          <div style={S.orb}><div style={S.orbCore} /><div style={S.orbRing} /></div>
          <div style={S.phaseText}>{phases[Math.min(creationPhase, phases.length - 1)]}</div>
          <div style={S.dots}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} style={{ ...S.dot, background: i <= creationPhase ? "#C6A87D" : "#333" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── RESULTS ───
  if (stage === "results") {
    return (
      <div style={S.screen}>
        <div style={{ ...S.center, maxWidth: 640, padding: "40px 16px 80px", alignItems: "stretch" }}>
          <div style={S.brand}>YOURS TRULY</div>

          {genError && (
            <div style={{ background: "#1a1111", border: "1px solid #441111", padding: 16, borderRadius: 6, marginBottom: 24, fontSize: 13, color: "#cc6666", fontFamily: "sans-serif" }}>
              Error: {genError}. Check your OpenAI API key and try again.
            </div>
          )}

          {designData && (
            <>
              <h1 style={{ fontSize: 42, fontWeight: 300, color: "#E8DDD3", margin: "0 0 8px", textAlign: "center", letterSpacing: "0.04em" }}>
                {designData.name}
              </h1>
              <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#C6A87D", textTransform: "uppercase", fontFamily: "sans-serif", textAlign: "center", marginBottom: 32 }}>
                {designData.mood}
              </div>

              {/* ─── DESIGN IMAGES ─── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 32 }}>
                {/* Sculpture */}
                <div>
                  <div style={S.imageFrame}>
                    {sculptureImg ? (
                      <img src={sculptureImg} alt="Sculpture" style={S.designImg} />
                    ) : (
                      <div style={S.placeholder}>
                        <div style={{ color: "#C6A87D", fontSize: 28, opacity: 0.4 }}>◇</div>
                        <div style={{ color: "#555", fontSize: 10, fontFamily: "sans-serif" }}>Generating...</div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: "#E8DDD3", marginTop: 8 }}>3D Sculpture</div>
                  <div style={{ fontSize: 11, color: "#666", fontFamily: "sans-serif" }}>Crystal render on black marble</div>
                </div>

                {/* Acrylic */}
                <div>
                  <div style={S.imageFrame}>
                    {plaqueImg ? (
                      <img src={plaqueImg} alt="Acrylic" style={S.designImg} />
                    ) : (
                      <div style={S.placeholder}>
                        <div style={{ color: "#C6A87D", fontSize: 28, opacity: 0.4 }}>◈</div>
                        <div style={{ color: "#555", fontSize: 10, fontFamily: "sans-serif" }}>Generating...</div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: "#E8DDD3", marginTop: 8 }}>Acrylic Print</div>
                  <div style={{ fontSize: 11, color: "#666", fontFamily: "sans-serif" }}>UV printed on crystal-clear acrylic</div>
                </div>
              </div>

              {/* ─── ELEMENT MAPPINGS ─── */}
              <div style={{ width: "100%", marginBottom: 24 }}>
                <div style={S.sectionLabel}>HOW EACH ELEMENT WAS ENCODED</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {designData.elementMappings?.map((em, i) => (
                    <div key={i} style={{ padding: "10px 14px", background: "#111", border: "1px solid #1A1A1A", borderRadius: 6 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: "#C6A87D", fontFamily: "sans-serif", fontWeight: 500 }}>{em.element}</span>
                        <span style={{ fontSize: 11, color: "#666", fontFamily: "sans-serif" }}>→ {em.mappedTo}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#777", lineHeight: 1.5 }}>{em.tripleMatch}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ─── POETIC NOTE ─── */}
              {designData.poeticNote && (
                <div style={{ textAlign: "center", padding: "16px 20px", margin: "0 0 24px", maxWidth: 420, alignSelf: "center" }}>
                  <p style={{ fontSize: 13, lineHeight: 1.8, color: "#888", fontStyle: "italic", margin: 0 }}>{designData.poeticNote}</p>
                </div>
              )}

              <div style={S.divider} />

              {/* ─── MEDIUM SELECTION ─── */}
              <div style={{ width: "100%" }}>
                <div style={S.sectionLabel}>CHOOSE YOUR MEDIUM</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { name: "Digital Design", price: "$29", desc: "High-res download" },
                    { name: "Museum Poster", price: "$79", desc: "Matte archival paper" },
                    { name: 'Acrylic Print 8×10"', price: "$79", desc: "Crystal-clear with stand" },
                    { name: 'Acrylic Print 12×16"', price: "$119", desc: "Wall-mounted" },
                    { name: "Crystal ArtPix 3D", price: "$149", desc: "Laser-etched crystal" },
                  ].map((m, i) => (
                    <button key={i} style={S.mediumBtn}>
                      <span style={{ flex: 1, fontSize: 13.5, color: "#E8DDD3", textAlign: "left" }}>{m.name}</span>
                      <span style={{ fontSize: 14, color: "#C6A87D", fontFamily: "sans-serif", fontWeight: 500, minWidth: 50, textAlign: "right" }}>{m.price}</span>
                      <span style={{ fontSize: 11, color: "#555", fontFamily: "sans-serif", minWidth: 130, textAlign: "right" }}>{m.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ─── PROMPTS (for debugging / manual generation) ─── */}
              <details style={{ width: "100%", marginTop: 32 }}>
                <summary style={{ fontSize: 11, color: "#444", fontFamily: "sans-serif", cursor: "pointer", letterSpacing: "0.1em" }}>VIEW GENERATED PROMPTS</summary>
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 10, color: "#C6A87D", fontFamily: "sans-serif", letterSpacing: "0.1em", marginBottom: 6 }}>SCULPTURE PROMPT:</div>
                  <div style={{ fontSize: 11, color: "#666", lineHeight: 1.6, padding: 12, background: "#0E0E0E", borderRadius: 4, marginBottom: 16, wordBreak: "break-word" }}>{designData.sculpturePrompt}</div>
                  <div style={{ fontSize: 10, color: "#C6A87D", fontFamily: "sans-serif", letterSpacing: "0.1em", marginBottom: 6 }}>ACRYLIC PROMPT:</div>
                  <div style={{ fontSize: 11, color: "#666", lineHeight: 1.6, padding: 12, background: "#0E0E0E", borderRadius: 4, wordBreak: "break-word" }}>{designData.plaquePrompt}</div>
                </div>
              </details>
            </>
          )}

          <button
            onClick={() => {
              setStage("opening");
              setMessages([]);
              setDesignData(null);
              setSculptureImg(null);
              setPlaqueImg(null);
              setInput("");
              setCreationPhase(0);
              setGenError(null);
            }}
            style={S.resetBtn}
          >
            Create another
          </button>
        </div>
      </div>
    );
  }

  // ─── CHAT ───
  return (
    <div style={S.screen}>
      <div style={S.chatWrap}>
        <div style={S.brand}>YOURS TRULY</div>
        <div style={S.chatScroll}>
          {messages.map((msg, i) => (
            <div key={i} style={{ ...S.msgRow, alignSelf: msg.role === "user" ? "flex-end" : "flex-start" }}>
              {msg.role === "assistant" && <div style={S.artistTag}>Designer</div>}
              <div style={{ ...S.bubble, ...(msg.role === "user" ? S.userBubble : S.aiBubble) }}>{msg.content}</div>
            </div>
          ))}
          {loading && (
            <div style={{ ...S.msgRow, alignSelf: "flex-start" }}>
              <div style={S.artistTag}>Designer</div>
              <div style={{ ...S.bubble, ...S.aiBubble, color: "#555" }}>· · ·</div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div style={S.chatBottom}>
          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Tell me more..." style={S.chatInput} disabled={loading} />
          <button onClick={handleSend} style={{ ...S.sendBtn, opacity: input.trim() && !loading ? 1 : 0.3 }} disabled={loading}>→</button>
        </div>
      </div>
    </div>
  );
}

// ─── STYLES ───
const S = {
  screen: { minHeight: "100vh", background: "#0A0A0A", display: "flex", justifyContent: "center", alignItems: "center", fontFamily: "Georgia, 'Times New Roman', serif" },
  center: { display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: 600, padding: "40px 24px" },
  brand: { fontSize: 9, letterSpacing: "0.4em", color: "#444", textTransform: "uppercase", fontFamily: "sans-serif", textAlign: "center", marginBottom: 40 },
  heroText: { fontSize: 32, fontWeight: 300, color: "#E8DDD3", margin: "0 0 48px", letterSpacing: "0.02em", textAlign: "center", lineHeight: 1.4 },
  inputRow: { display: "flex", gap: 12, width: "100%", maxWidth: 460 },
  textInput: { flex: 1, padding: "16px 20px", background: "#141414", border: "1px solid #252525", borderRadius: 4, color: "#E8DDD3", fontSize: 15, fontFamily: "Georgia, serif", outline: "none" },
  sendBtn: { padding: "14px 20px", background: "#C6A87D", color: "#0A0A0A", border: "none", borderRadius: 4, fontSize: 18, cursor: "pointer", fontWeight: 600, transition: "opacity 0.2s" },

  // Creating
  orb: { width: 120, height: 120, position: "relative", marginBottom: 40, display: "flex", alignItems: "center", justifyContent: "center" },
  orbCore: { width: 40, height: 40, borderRadius: "50%", background: "radial-gradient(circle, #C6A87D44, #C6A87D11)", boxShadow: "0 0 40px #C6A87D22" },
  orbRing: { position: "absolute", width: 100, height: 100, borderRadius: "50%", border: "1px solid #C6A87D22" },
  phaseText: { fontSize: 15, color: "#888", fontStyle: "italic", marginBottom: 24, textAlign: "center" },
  dots: { display: "flex", gap: 8 },
  dot: { width: 6, height: 6, borderRadius: "50%", transition: "background 0.5s ease" },

  // Results
  divider: { width: 40, height: 1, background: "#C6A87D", opacity: 0.3, margin: "28px auto" },
  sectionLabel: { fontSize: 9, letterSpacing: "0.25em", color: "#555", textTransform: "uppercase", fontFamily: "sans-serif", marginBottom: 16, textAlign: "center" },
  imageFrame: { aspectRatio: "3/4", background: "#0E0E0E", border: "1px solid #1A1A1A", borderRadius: 6, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" },
  designImg: { width: "100%", height: "100%", objectFit: "cover" },
  placeholder: { display: "flex", flexDirection: "column", alignItems: "center", gap: 8 },
  mediumBtn: { display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", background: "#111", border: "1px solid #1A1A1A", borderRadius: 6, cursor: "pointer", fontFamily: "Georgia, serif" },
  resetBtn: { marginTop: 32, padding: "10px 24px", background: "transparent", border: "1px solid #333", borderRadius: 4, color: "#666", fontSize: 12, fontFamily: "sans-serif", letterSpacing: "0.1em", cursor: "pointer" },

  // Chat
  chatWrap: { display: "flex", flexDirection: "column", width: "100%", maxWidth: 540, height: "100vh", padding: "24px 16px 16px", boxSizing: "border-box" },
  chatScroll: { flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 20, paddingBottom: 20, scrollbarWidth: "none" },
  msgRow: { display: "flex", flexDirection: "column", maxWidth: "85%" },
  artistTag: { fontSize: 9, letterSpacing: "0.2em", color: "#C6A87D", textTransform: "uppercase", fontFamily: "sans-serif", marginBottom: 6, marginLeft: 4 },
  bubble: { padding: "14px 18px", borderRadius: 12, fontSize: 14.5, lineHeight: 1.7 },
  userBubble: { background: "#1A1A1A", color: "#E8DDD3", borderBottomRightRadius: 4 },
  aiBubble: { background: "transparent", border: "1px solid #1E1E1E", color: "#BBB", borderBottomLeftRadius: 4 },
  chatBottom: { display: "flex", gap: 10, paddingTop: 12, borderTop: "1px solid #1A1A1A" },
  chatInput: { flex: 1, padding: "14px 18px", background: "#111", border: "1px solid #222", borderRadius: 4, color: "#E8DDD3", fontSize: 14.5, fontFamily: "Georgia, serif", outline: "none" },
};

export default Studio;
